import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material';
import { InspectorService } from '../../../inspector.service';

interface ItemData {
  phoneNumber: string;
  email: string;
  inspectorId: string;
}

@Component({
  selector: 'app-shift-item-info',
  templateUrl: './add-email-and-phone.component.html',
  styleUrls: ['./add-email-and-phone.component.scss']
})
export class AddEmailAndPhoneComponent implements OnInit {

  loading: Boolean = false;
  phoneNumber = '';
  email = '';
  showPhoneNumberField: Boolean = true;
  showEmailField: Boolean = true;

  constructor(
    public dialogRef: MatDialogRef<AddEmailAndPhoneComponent>,
    @Inject(MAT_DIALOG_DATA) public item: ItemData,
    public dialog: MatDialog,
    private inspectorService: InspectorService,
  ) { }

  ngOnInit() {
    this.showPhoneNumberField = this.item.phoneNumber === "";
    this.showEmailField = this.item.email === "";
    if (!this.showPhoneNumberField) {
      this.phoneNumber = this.item.phoneNumber;
    }

    if (!this.showEmailField) {
      this.email = this.item.email;
    }
  }

  onSubmit() {
    const phoneNumberRegex = /\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/
    if (!phoneNumberRegex.test(this.phoneNumber)) return;
    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!emailRegex.test(this.email)) return;
    this.loading = true;
    this.inspectorService.updateInspectorEmailAndPhone(this.email, this.phoneNumber, this.item.inspectorId)
      .subscribe(() => {
        this.loading = false;
        this.dialogRef.close({
          result: 'Submitted',
          email: this.email,
          phoneNumber: this.phoneNumber,
        });
      },
      (error) => {
        console.log({ error })
        this.loading = false;
        this.dialogRef.close({
          result: 'Errored',
        });
      })
  }

  // Source: https://github.com/grommet/grommet/blob/ddc243ec73241ef24bfd526221cab9002e3f27b5/src/js/components/MaskedInput/MaskedInput.js#L27
  private parseValueForMask (mask: any, value: String) {
    // break the value up into mask parts
    const valueParts = []; // { part, beginIndex, endIndex }
    let valueIndex = 0;
    let maskIndex = 0;
    while (
      value !== undefined &&
      valueIndex < value.length &&
      maskIndex < mask.length
    ) {
      const item: any = mask[maskIndex];
      let found;
      if (item.fixed) {
        const { length } = item.fixed;

        // grab however much of value (starting at valueIndex) matches
        // item.fixed. If none matches it and there is more in value
        // add in the fixed item.
        let matching = 0;
        while (
          matching < length &&
          value[valueIndex + matching] === item.fixed[matching]
        ) {
          matching += 1;
        }

        if (matching > 0) {
          let part = value.slice(valueIndex, valueIndex + matching);
          if (valueIndex + matching < value.length) {
            // matched part of the fixed portion but there's more stuff
            // after it. Go ahead and fill in the entire fixed chunk
            part = item.fixed;
          }
          valueParts.push({
            part,
            beginIndex: valueIndex,
            endIndex: valueIndex + matching - 1,
          });
          valueIndex += matching;
        } else {
          valueParts.push({
            part: item.fixed,
            beginIndex: valueIndex,
            endIndex: valueIndex + length - 1,
          });
        }

        maskIndex += 1;
        found = true;
      } else if (item.options && item.restrictToOptions !== false) {
        // reverse assuming larger is later
        found = item.options
          .slice(0)
          .reverse()
          // eslint-disable-next-line no-loop-func
          .some((option) => {
            const { length } = option;
            const part = value.slice(valueIndex, valueIndex + length);
            if (part === option) {
              valueParts.push({
                part,
                beginIndex: valueIndex,
                endIndex: valueIndex + length - 1,
              });
              valueIndex += length;
              maskIndex += 1;
              return true;
            }
            return false;
          });
      }
      if (!found) {
        if (item.regexp) {
          const minLength =
            (Array.isArray(item.length) && item.length[0]) || item.length || 1;
          const maxLength =
            (Array.isArray(item.length) && item.length[1]) ||
            item.length ||
            value.length - valueIndex;
          let length = maxLength;
          while (!found && length >= minLength) {
            const part = value.slice(valueIndex, valueIndex + length);
            if (item.regexp.test(part)) {
              valueParts.push({
                part,
                beginIndex: valueIndex,
                endIndex: valueIndex + length - 1,
              });
              valueIndex += length;
              maskIndex += 1;
              found = true;
            }
            length -= 1;
          }
          if (!found) {
            valueIndex = value.length;
          }
        } else {
          const length = Array.isArray(item.length)
            ? item.length[1]
            : item.length || value.length - valueIndex;
          const part = value.slice(valueIndex, valueIndex + length);
          valueParts.push({
            part,
            beginIndex: valueIndex,
            endIndex: valueIndex + length - 1,
          });
          valueIndex += length;
          maskIndex += 1;
        }
      }
    }
    return valueParts;
  }

  handleChange(event) {
    const { name, value } = event.target;
    if (name === "phoneNumber") {
      const phoneNumberMask = [
        { fixed: '(' },
        {
          length: 3,
          regexp: /^[0-9]{1,3}$/,
        },
        { fixed: ')' },
        { fixed: ' ' },
        {
          length: 3,
          regexp: /^[0-9]{1,3}$/,
        },
        { fixed: '-' },
        {
          length: 4,
          regexp: /^[0-9]{1,4}$/,
        },
      ];
      const valueParts = this.parseValueForMask(phoneNumberMask, value);
      this.phoneNumber = valueParts.map((part) => part.part).join('');
    } else if (name === "email") {
      const emailMask = [
        {
          regexp: /^[\w\-_.]+$/,
        },
        { fixed: '@' },
        {
          regexp: /^[\w]+$/,
        },
        { fixed: '.' },
        {
          regexp: /^[\w]+$/,
        },
      ];
      const valueParts = this.parseValueForMask(emailMask, value);
      this.email = valueParts.map((part) => part.part).join('');
    }
  }

  closeClicked() {
    this.dialogRef.close({ result: 'cancelled' });
  }

}
